import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Badge, ButtonGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { BookStatusBadge } from '../components/BookProgressBar';
import { getNativeLanguageName } from '../constants/languageNames';

const PAUSED_STATUSES = new Set(['awaiting_approval', 'story_generated']);
const ACTIVE_STATUSES = new Set(['pending', 'images_generating', 'story_qa', 'images_qa', 'assembling']);

function jobCategory(job) {
    if (job.status === 'completed') return 'completed';
    if (job.status === 'failed')    return 'failed';
    if (PAUSED_STATUSES.has(job.status)) return 'paused';
    if (ACTIVE_STATUSES.has(job.status)) return 'active';
    return 'other';
}

function AdminGenaiJobListScreen() {
    const navigate = useNavigate();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
            return;
        }
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        axios.get('/api/genai/admin/jobs/', config)
            .then(res => { setJobs(res.data); setLoading(false); })
            .catch(err => {
                setError(err.response?.data?.error || err.message);
                setLoading(false);
            });
    }, [navigate, userInfo]);

    const counts = useMemo(() => {
        const c = { all: jobs.length, active: 0, paused: 0, completed: 0, failed: 0 };
        jobs.forEach(j => { const cat = jobCategory(j); if (cat in c) c[cat]++; });
        return c;
    }, [jobs]);

    const filtered = useMemo(() =>
        filter === 'all' ? jobs : jobs.filter(j => jobCategory(j) === filter),
    [jobs, filter]);

    const shortId = (uuid) => uuid ? uuid.slice(0, 8) + '…' : '—';

    const extractName = (inputData) => {
        if (!inputData) return '—';
        if (inputData.characters && inputData.characters.length > 0) {
            return inputData.characters[0].name || '—';
        }
        return inputData.child_name || '—';
    };

    return (
        <div>
            <h1>AI Book Generation Jobs</h1>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">All user book generation jobs across the platform.</p>
                <ButtonGroup size="sm">
                    {[
                        { key: 'all',       label: 'All',       variant: 'outline-secondary' },
                        { key: 'active',    label: '⚙️ Active',  variant: 'outline-info'      },
                        { key: 'paused',    label: '⏸ Paused',  variant: 'outline-warning'   },
                        { key: 'completed', label: '✅ Done',    variant: 'outline-success'   },
                        { key: 'failed',    label: '❌ Failed',  variant: 'outline-danger'    },
                    ].map(({ key, label, variant }) => (
                        <Button
                            key={key}
                            variant={filter === key ? variant.replace('outline-', '') : variant}
                            onClick={() => setFilter(key)}
                        >
                            {label}{' '}
                            <Badge bg="light" text="dark">{counts[key] ?? 0}</Badge>
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {loading ? <Loader /> : error ? <Message variant="danger">{error}</Message> : (
                <Table striped bordered hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>Created</th>
                            <th>User</th>
                            <th>Job ID</th>
                            <th>Child / Main Char.</th>
                            <th>Language</th>
                            <th>Status</th>
                            <th>Step</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(job => (
                            <tr key={job.job_id}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {new Date(job.created_at).toLocaleDateString('en-GB', {
                                        day: '2-digit', month: 'short', year: 'numeric'
                                    })}
                                </td>
                                <td>{job.user?.email || job.user?.username || '—'}</td>
                                <td>
                                    <code title={job.job_id}>{shortId(job.job_id)}</code>
                                </td>
                                <td>{extractName(job.input_data)}</td>
                                <td>{getNativeLanguageName(job.language || (job.input_data?.language) || 'English', 'English')}</td>
                                <td><BookStatusBadge status={job.status} currentStep={job.current_step} /></td>
                                <td>
                                    <small className="text-muted">{job.current_step || job.status}</small>
                                </td>
                                <td>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => navigate(`/admin/genai/jobs/${job.job_id}`)}
                                    >
                                        View
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan="8" className="text-center text-muted">No generation jobs found.</td></tr>
                        )}
                    </tbody>
                </Table>
            )}
        </div>
    );
}

export default AdminGenaiJobListScreen;
